import * as React from 'react';

/**
 * Single-key shortcuts, ignored while somebody is typing.
 *
 * The guard is the whole reason this is a hook rather than an addEventListener in
 * each screen: without it, typing "n" into a note fires "new entry", and the
 * feature becomes something people ask you to turn off. Anything with a modifier
 * is left alone too - those belong to the browser.
 */
export function useShortcuts(map: Record<string, () => void>): void {
  // Held in a ref so the effect does not resubscribe on every render, which would
  // otherwise happen constantly - the handlers are usually inline closures.
  const latest = React.useRef(map);
  latest.current = map;

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
          // Radix menus and comboboxes handle their own keys.
          target.closest('[role="dialog"],[role="listbox"],[role="menu"]'))
      ) {
        return;
      }

      const handler = latest.current[event.key];
      if (!handler) return;
      event.preventDefault();
      handler();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

/** The shortcuts every period-based screen shares, so they are the same everywhere. */
export function usePeriodShortcuts(options: {
  previous: () => void;
  next: () => void;
  today?: () => void;
}): void {
  useShortcuts({
    // Both the arrows and j/k: one is obvious, the other is what anybody who
    // lives in a terminal will try first.
    ArrowLeft: options.previous,
    ArrowRight: options.next,
    j: options.previous,
    k: options.next,
    ...(options.today ? { t: options.today } : {}),
  });
}
